import {
  LinkAccountSubtype,
  LinkAccountType,
  LinkAccountVerificationStatus,
  LinkErrorCode,
  LinkErrorType,
  LinkExit,
  LinkExitMetadataStatus,
  LinkSuccess,
} from "react-native-plaid-link-sdk"
import { PlaidLinkOptions, usePlaidLink as useLegacyPlaidLink } from "react-plaid-link"
import { retrieveValue, Sentry } from "../../utils"
import { useCachedQueryData } from "../useCachedQueryData"
import { MOCK } from "../../constants"
import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { PlaidLinkOnEventMetadata } from "react-plaid-link/src/types"
import { PlaidContext } from "../../context/plaidContext"

function getPlaidConfig(props: {
  onSuccess: (success: LinkSuccess) => void
  onExit: (exit: LinkExit) => void
  token?: string
}): PlaidLinkOptions {
  return {
    onSuccess: (public_token, metadata) =>
      props.onSuccess({
        publicToken: public_token ?? "",
        metadata: {
          accounts: metadata.accounts.map((a) => ({
            id: a.id,
            name: a.name,
            mask: a.mask ?? undefined,
            type: a.type as LinkAccountType,
            subtype: a.subtype as LinkAccountSubtype,
            verificationStatus: a.verification_status as LinkAccountVerificationStatus,
          })),
          linkSessionId: metadata.link_session_id,
          institution: metadata.institution
            ? {
                id: metadata.institution.institution_id,
                name: metadata.institution.name,
              }
            : undefined,
        },
      }),
    onExit: (err, metadata) =>
      props.onExit({
        error: err
          ? {
              errorCode: err.error_code as LinkErrorCode,
              errorType: err.error_type as LinkErrorType,
              errorMessage: err.error_message ?? "",
              displayMessage: err.display_message ?? undefined,
            }
          : undefined,
        metadata: {
          linkSessionId: metadata.link_session_id,
          institution: metadata.institution
            ? {
                id: metadata.institution.institution_id,
                name: metadata.institution.name,
              }
            : undefined,
          requestId: metadata.request_id,
          status: metadata.status as LinkExitMetadataStatus,
        },
      }),
    token: props.token || "",
  }
}

export function usePlaidLayer(props: {
  onSuccess: (success: LinkSuccess) => void
  onExit: (exit: LinkExit) => void
  fallback: () => void
  token: string
  onLayerReady: (val: boolean) => void
}) {
  const endTime = useRef(0)
  const [plaidLoaded, setPlaidLoaded] = useState(false)
  const [layerReady, setLayerReady] = useState(false)
  const [triggerLayerFlow, setTriggerLayerFlow] = useState(false)
  // Do not feed Start's link token into react-plaid-link until the bank step
  // calls startLayer. Otherwise LAYER_NOT_AVAILABLE fires during OTP (token is
  // prefetched at phone Start) and /Account/LinkToken runs with no verificationId.
  const [sessionToken, setSessionToken] = useState("")
  const { openInOnReady, appendEvent, setIsLayerSessionLoading } = useContext(PlaidContext)

  // react-plaid-link only recreates its handler when the token changes, so
  // callbacks from the phone-auth render (before onboarding exists) would
  // otherwise be frozen for the whole Layer session.
  const onSuccessRef = useRef(props.onSuccess)
  const onExitRef = useRef(props.onExit)
  const fallbackRef = useRef(props.fallback)
  const onLayerReadyRef = useRef(props.onLayerReady)
  onSuccessRef.current = props.onSuccess
  onExitRef.current = props.onExit
  fallbackRef.current = props.fallback
  onLayerReadyRef.current = props.onLayerReady

  const signUpPhoneNumber = useCachedQueryData<string>("signUpPhoneNumber")

  const submitPhoneNumber = useCallback(
    (submitFn: (data: { phone_number: string }) => void) => {
      if (!signUpPhoneNumber) {
        return
      }
      submitFn({ phone_number: signUpPhoneNumber })
    },
    [signUpPhoneNumber]
  )

  const runFallback = useCallback(() => {
    setSessionToken("")
    setTriggerLayerFlow(false)
    setLayerReady(false)
    fallbackRef.current()
  }, [])

  const config = {
    ...getPlaidConfig({
      onSuccess: (success) => onSuccessRef.current(success),
      onExit: (exit) => onExitRef.current(exit),
      token: sessionToken,
    }),
    onLoad: () => {
      submitPhoneNumber(submitCbRef.current)
      appendEvent(`submitting ${signUpPhoneNumber} in onLoad. ${submitCbRef.current}`)
      setPlaidLoaded(true)
    },
    onEvent: (eventName: string, meta: PlaidLinkOnEventMetadata) => {
      appendEvent(eventName)
      endTime.current = performance.now()
      switch (eventName) {
        case "LAYER_READY":
          setTriggerLayerFlow(true)
          setLayerReady(true)
          setIsLayerSessionLoading(false)
          appendEvent(`Opening Layer ${openCbRef.current.toString()}`)
          onLayerReadyRef.current(true)
          if (openInOnReady.current) {
            openCbRef.current()
          }
          break
        case "LAYER_NOT_AVAILABLE":
          setIsLayerSessionLoading(false)
          runFallback()
          break
        case "ERROR":
          Sentry.captureException("Plaid Layer failed", { extra: { plaidMeta: meta } })
          setIsLayerSessionLoading(false)
          runFallback()
          break
        case "EXIT":
          Sentry.captureException("Plaid Layer exited prematurely. Fallback activated.", {
            extra: { plaidMeta: meta },
          })
          setIsLayerSessionLoading(false)
          onLayerReadyRef.current(false)
          setTriggerLayerFlow(false)
          break
        default:
          break
      }
    },
  }

  const { open, submit } = useLegacyPlaidLink(config)
  const openCbRef = useRef<typeof open>(open)
  const submitCbRef = useRef<typeof submit>(submit)

  useEffect(() => {
    openCbRef.current = open
    submitCbRef.current = submit
  }, [open, submit])

  const openLayerHandler = useCallback(() => {
    if (layerReady && plaidLoaded && document.visibilityState === "visible" && triggerLayerFlow) {
      submitPhoneNumber(submit)
    }
  }, [layerReady, plaidLoaded, submit, submitPhoneNumber, triggerLayerFlow])

  useEffect(() => {
    window.addEventListener("visibilitychange", openLayerHandler)

    return () => {
      window.removeEventListener("visibilitychange", openLayerHandler)
    }
  }, [openLayerHandler])

  useEffect(() => {
    openLayerHandler()
  }, [openLayerHandler])

  useEffect(() => {
    function func(event: MessageEvent<unknown>) {
      // hack, do we need it?
      if (event.data === '{"action":"plaid_link-1::ready"}') {
        appendEvent(event.data)
        submitPhoneNumber(submit)
      }
    }

    window.addEventListener("message", func)

    return () => {
      window.removeEventListener("message", func)
    }
  }, [submit, submitPhoneNumber, appendEvent])

  const startLayer = async ({ token }: { token: string; phoneNumber?: string | null }) => {
    const demo = (await retrieveValue("mode")) === "demo"
    if (MOCK || demo) {
      onSuccessRef.current({
        publicToken: token,
        metadata: {
          institution: { id: "", name: "" },
          accounts: [],
          linkSessionId: "",
        },
      })
      return
    }
    appendEvent("Layer triggered")
    // Arm react-plaid-link only now (bank step), not when Start prefetched the token.
    setSessionToken(token)
    setTriggerLayerFlow(true)
    // fake numbers for testing
    // +14155550123
    // +14155550015
    // web implementation of Layer is buggy
  }

  return {
    startLayer,
    open: openCbRef.current,
  }
}

export function dismissLayer() {
  // noop
}
